import type { LogbookEntry } from '@/lib/db';

import { caa1373Row, capacityHoursForRole } from './caa1373';
import { formatDecimalHours } from './time';

/**
 * Whether a row's times add up against each other.
 *
 * A logbook row is not a list of independent numbers: the same hour appears
 * in several columns at once, and the columns it may appear in are fixed.
 * `NZ-LOGBOOK.md` §3 splits flight time three ways over the *same* hours —
 * day/night, capacity, instrument — so a row where those splits disagree has
 * a typo in it somewhere, and a typo in a logbook propagates into every
 * total, currency window and printed page that folds over it.
 *
 * This is a warning, not a gate, for the same reason the takeoff/landing
 * check in `review.ts` is: the pilot knows what they flew and the app does
 * not. The grid paints the cells that disagree red, says which sum is out,
 * and still lets the entry be approved.
 */

/** Hours are stored to two places, so anything under half of the last place is rounding, not a disagreement. */
const EPSILON = 0.005;

export const FLIGHT_TIME_FIELDS = ['time.dayHr', 'time.nightHr'] as const;
export const CAPACITY_FIELDS = [
  'time.picHr',
  'time.commandPracticeHr',
  'time.dualHr',
  'time.coPilotHr',
] as const;
export const INSTRUMENT_FIELDS = ['time.actualInstrumentHr', 'time.simulatedInstrumentHr'] as const;
export const INSTRUCTOR_FIELD = 'time.instructorHr';
export const IFR_CROSS_COUNTRY_FIELD = 'time.ifrCrossCountryHr';
export const APPROACH_FIELDS = ['approaches.nonPrecision', 'approaches.precision'] as const;
export const ROLE_FIELD = 'role';
export const SYNTHETIC_FIELD = 'time.groundInstrumentHr';

export interface TimeCheckIssue {
  /**
   * Every column the failing sum touched, not just the one that looks wrong —
   * the app cannot know which number is the typo, and painting only one of
   * them would be an accusation it can't back up.
   */
  fields: readonly string[];
  message: string;
}

/**
 * The disagreements in one row, in the order a pilot would want to fix them.
 *
 * A row where everything is zero — a freshly added entry — has nothing to
 * disagree about and comes back clean, which is what keeps a blank logbook
 * from opening in red.
 */
export function timeConsistencyIssues(entry: LogbookEntry): TimeCheckIssue[] {
  const time = entry.time;
  const flightTotal = time.dayHr + time.nightHr;
  /*
   * A ground instrument session has no day or night time by definition
   * (`NZ-LOGBOOK.md` §3: simulator time is "logged separately from aircraft
   * time"), so on a sim row the hours everything else is measured against
   * come from that column instead. With both at zero the yardstick is still
   * Day/Night, because an empty Day cell is what the pilot has to fix.
   */
  const isSimSession = flightTotal <= EPSILON && time.groundInstrumentHr > EPSILON;
  const flown = isSimSession ? time.groundInstrumentHr : flightTotal;
  const flownFields: readonly string[] = isSimSession ? [SYNTHETIC_FIELD] : FLIGHT_TIME_FIELDS;
  const flownText = formatDecimalHours(flown);

  const issues: TimeCheckIssue[] = [];

  /*
   * Capacity is a partition, not a set of tick boxes: every hour is flown in
   * exactly one of PIC, Command Practice, Dual or Co-Pilot, so they sum to
   * the flight. Left out
   * on purpose is Flight Instructor, which is not a fifth capacity — an
   * instructor logs the same hour as PIC *and* as instructing, and adding it here would
   * flag every dual instructional flight ever logged.
   */
  const capacity = time.picHr + time.commandPracticeHr + time.dualHr + time.coPilotHr;
  if (capacity > EPSILON && Math.abs(capacity - flown) > EPSILON) {
    issues.push({
      fields: [...CAPACITY_FIELDS, ...flownFields],
      message: `PIC + Command Practice + Dual + Co-Pilot comes to ${formatDecimalHours(capacity)}, but ${flownText} was flown.`,
    });
  }

  const instrument = time.actualInstrumentHr + time.simulatedInstrumentHr;
  if (instrument - flown > EPSILON) {
    issues.push({
      fields: [...INSTRUMENT_FIELDS, ...flownFields],
      message: `Actual + Simulated instrument comes to ${formatDecimalHours(instrument)}, more than the ${flownText} flown.`,
    });
  }

  if (time.instructorHr - flown > EPSILON) {
    issues.push({
      fields: [INSTRUCTOR_FIELD, ...flownFields],
      message: `Flight Instructor is ${formatDecimalHours(time.instructorHr)}, more than the ${flownText} flown.`,
    });
  }

  if (time.ifrCrossCountryHr - flown > EPSILON) {
    issues.push({
      fields: [IFR_CROSS_COUNTRY_FIELD, ...flownFields],
      message: `IFR Cross Country is ${formatDecimalHours(time.ifrCrossCountryHr)}, more than the ${flownText} flown.`,
    });
  }

  /*
   * An approach needs a flight to have been flown on. The count is not
   * checked against the instrument time — an approach flown in VMC for
   * practice logs no actual instrument time at all, and plenty of real
   * approaches take four minutes on a two-hour flight — but a row with
   * approaches and no hours anywhere is a row where something was typed into
   * the wrong line.
   */
  const approaches = entry.approaches.nonPrecision + entry.approaches.precision;
  if (approaches > 0 && flown <= EPSILON) {
    issues.push({
      fields: [...APPROACH_FIELDS, ...flownFields],
      message: `${String(approaches)} instrument ${approaches === 1 ? 'approach' : 'approaches'} on a row with no time flown.`,
    });
  }

  /*
   * The role and the capacity hours are two ways of saying the same thing —
   * the entry form writes both — so a row where they disagree has been hand
   * edited since, and the CAA columns will follow the hours rather than the
   * role. Said out loud rather than silently corrected, because either one
   * could be the thing the pilot meant: `NZ-LOGBOOK.md` §2.4.
   */
  if (entry.role !== undefined && flightTotal > EPSILON) {
    const expected = capacityHoursForRole(entry.role, flightTotal);
    const disagrees = (['picHr', 'commandPracticeHr', 'dualHr', 'coPilotHr'] as const).some(
      (key) => Math.abs(time[key] - expected[key]) > EPSILON,
    );
    if (disagrees) {
      issues.push({
        fields: [ROLE_FIELD, ...CAPACITY_FIELDS],
        message: `The hours no longer match the role recorded for this flight (${entry.role}).`,
      });
    }
  }

  /*
   * CAA 1373 prints Co-Pilot and Command Practice under MULTI ENGINE only.
   * Hours in either on a single-engine row have nowhere to print, so the
   * pilot is told rather than having them quietly folded into Dual or PIC —
   * see `Caa1373Row.unplacedHr`.
   */
  const { unplacedHr } = caa1373Row(entry);
  if (unplacedHr > EPSILON) {
    issues.push({
      fields: ['time.coPilotHr', 'time.commandPracticeHr', 'time.engineClass'],
      message: `${formatDecimalHours(unplacedHr)} of Co-Pilot or Command Practice time on a single-engine row. CAA 1373 prints those columns under MULTI ENGINE only. It will not appear on the printed page.`,
    });
  }

  if (flightTotal > EPSILON && time.groundInstrumentHr > EPSILON) {
    issues.push({
      fields: [SYNTHETIC_FIELD, ...FLIGHT_TIME_FIELDS],
      message:
        'Ground instrument time is logged separately from aircraft time. A row carrying both is two entries, not one.',
    });
  }

  return issues;
}
